import { profileHelp } from "./screenHelpContent";
import { appConfirm } from "./appDialogs";
import ReviewRadioGroup from "./ReviewRadioGroup";
import { useScreenHelp } from "./ScreenHelp";
import React, { useMemo, useState } from "react";
import {
  groupsForSite,
  selectionFor,
  addressReady,
  countryOf,
} from "./cqcFrameworks";
import CqcAddressForm from "./CqcAddressForm";
import {
  catalogue,
  blankProfile,
  reconcilePlan,
  suggestedPlan,
  scopeKey,
  serviceGuidance,
} from "./cqcProfileModel";
import { cqcApi } from "./cqcApi";
import "./cqc-workspace.css";

export default function CqcServiceProfile({ site, onSaved, onCancel }) {
  const [draft, setDraft] = useState(() => {
    const p = blankProfile(site),
      date = new Date();
    date.setUTCMonth(date.getUTCMonth() + 6);
    return reconcilePlan({
      ...p,
      reviewDate: p.reviewDate || date.toISOString().slice(0, 10),
    });
  });
  const [suggestionsApplied, setSuggestionsApplied] = useState(
    !!site.serviceProfile,
  );
  const [step, setStep] = useState(0),
    [confirmed, setConfirmed] = useState(false),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [search, setSearch] = useState("");
  const totals = useMemo(
    () =>
      Object.values(draft.decisions).reduce(
        (m, d) => ({ ...m, [d.mode]: (m[d.mode] || 0) + 1 }),
        {},
      ),
    [draft.decisions],
  );
  const groups = groupsForSite(site, draft),
    selected = selectionFor(draft, countryOf(site));
  function patch(values) {
    setDraft((d) => reconcilePlan({ ...d, ...values }));
    setConfirmed(false);
  }
  function decision(key, values) {
    setDraft((d) => ({
      ...d,
      decisions: { ...d.decisions, [key]: { ...d.decisions[key], ...values } },
    }));
    setConfirmed(false);
  }
  function goStep(next) {
    if (next === 2 && !suggestionsApplied) {
      patch({ decisions: suggestedPlan(draft) });
      setSuggestionsApplied(true);
    }
    setStep(next);
  }
  useScreenHelp(profileHelp(step, site.name), 50);
  async function save() {
    setBusy(true);
    setError("");
    try {
      const res = await cqcApi.saveServiceProfile(
        site._id,
        { ...draft, confirmed },
        site.profileRevision || 0,
      );
      if (onSaved) await onSaved(res.site);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }
  if (!addressReady(site))
    return <CqcAddressForm site={site} onSaved={onSaved} onCancel={onCancel} />;
  return (
    <div className="cqc-workspace">
      <h2>Service profile — {site.name}</h2>
      <p className="cqc-muted">
        {countryOf(site)} ·{" "}
        {site.source === "manual"
          ? site.registrationReference || "Registration reference not recorded"
          : `CQC location ${site.cqcLocationId}`}{" "}
        · Each selected organisation has its own evidence plan.
      </p>
      <div className="cqc-toolbar">
        {[
          "Organisation and services",
          "Functions provided",
          "Relevant evidence items",
        ].map((label, n) => (
          <button
            className={`cqc-button ${step === n ? "active" : ""}`}
            key={label}
            disabled={
              n > 0 && (!draft.serviceTypes.length || !!selected.issues.length)
            }
            onClick={() => goStep(n)}
          >
            {n + 1}. {label}
          </button>
        ))}
      </div>
      <div className="cqc-warning">
        National evidence plans support readiness; they do not replace the full
        source standards, service registration conditions or the regulator’s
        judgement.
      </div>
      {error && (
        <p role="alert" className="cqc-error">
          {error}
        </p>
      )}
      {step === 0 && (
        <section className="cqc-card">
          <h3>What does this organisation provide?</h3>
          <p>
            Choose all service types delivered at this service location. A mixed
            service can have more than one type.
          </p>
          <div className="cqc-service-options">
            {catalogue.services.map((s) => (
              <label className="cqc-service-option" key={s.id}>
                <input
                  type="checkbox"
                  checked={draft.serviceTypes.includes(s.id)}
                  onChange={(e) =>
                    patch({
                      serviceTypes: e.target.checked
                        ? [...draft.serviceTypes, s.id]
                        : draft.serviceTypes.filter((t) => t !== s.id),
                    })
                  }
                />
                {s.label}
              </label>
            ))}
          </div>
          <p>
            <label>
              Registration / oversight route{" "}
              <select
                aria-label="Registration / oversight route"
                value={draft.careSetting}
                onChange={(e) => patch({ careSetting: e.target.value })}
              >
                <option value="unknown">
                  Needs review / not relevant to care-only services
                </option>
                <option value="independent">
                  Independent provider / private clinic
                </option>
                <option value="nhs">NHS / statutory service</option>
                <option value="mixed">
                  More than one route (separate services)
                </option>
              </select>
            </label>
          </p>
          <p className="cqc-muted">
            Choose the registration/oversight route, not just who pays for care.
            An independently registered provider remains independent when
            providing NHS-funded treatment. Scottish NHS dental practices use
            CPI, including routine private provision; select multiple routes
            only for separate services. Social-care-only profiles do not need
            this setting.
          </p>
          <p>
            <strong>Registration information</strong>
          </p>
          <p>
            {(site.gacServiceTypes || []).join(", ") ||
              "No service types recorded"}
          </p>
          <p>
            {(site.regulatedActivities || []).join(", ") ||
              "No regulated activities recorded"}
          </p>
          <p className="cqc-muted">
            The profile describes actual services; it does not alter regulator
            registration or conditions.
          </p>
          <h4>Framework selected from country and service</h4>
          {selected.frameworks.map((f) => (
            <p key={f.id}>
              <a href={f.source} target="_blank" rel="noreferrer">
                {f.regulator} — {f.label}
              </a>
              {f.note && <span className="cqc-muted"> · {f.note}</span>}
            </p>
          ))}
          {selected.issues.map((issue) => (
            <p className="cqc-warning" key={issue}>
              {issue}
            </p>
          ))}
          <label>
            Service stage{" "}
            <select
              value={draft.stage}
              onChange={(e) => patch({ stage: e.target.value })}
            >
              <option value="operating">Operating</option>
              <option value="pre-opening">Preparing to open</option>
              <option value="temporarily-inactive">Temporarily inactive</option>
            </select>
          </label>
        </section>
      )}
      {step === 1 && (
        <section className="cqc-card">
          <h3>Which functions are provided here?</h3>
          <p>
            Answer for this location. “Needs review” keeps the responsibility visible
            for review; it never automatically excludes evidence.
          </p>
          {catalogue.functions.map((f) => (
            <ReviewRadioGroup key={f.id} label={f.label} value={draft.functions[f.id]}
              onChange={(value) => patch({ functions: { ...draft.functions, [f.id]: value } })} />
          ))}
          <p className="cqc-muted">
            A “No” answer changes the guidance. It does not automatically remove
            a broad quality statement that may still involve referral or
            escalation responsibilities.
          </p>
        </section>
      )}
      {step === 2 && (
        <section>
          <div className="cqc-card">
            <h3>Review the organisation’s evidence plan</h3>
            <p>
              Required items count towards readiness. Supporting items stay
              available without reducing readiness. Not applicable needs an
              organisation-specific reason.
            </p>
            <button
              className="cqc-button"
              onClick={async () => {
                if (
                  (await appConfirm(
                    "Replace this draft’s item selections with the suggested plan? Saved evidence and completion records will be retained.",
                    { title: "Use the suggested evidence plan?", confirmLabel: "Use suggested plan" },
                  ))
                )
                  patch({ decisions: suggestedPlan(draft) });
              }}
            >
              Use suggested plan for these services
            </button>
            <p>
              {totals.required || 0} required · {totals.supporting || 0}{" "}
              supporting · {totals["not-applicable"] || 0} not applicable
            </p>
            <input
              aria-label="Find a requirement"
              placeholder="Find a requirement…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          {groups.map((g) => (
            <section key={g.id}>
              <h3>{g.label}</h3>
              {g.items
                .filter((i) =>
                  i.label.toLowerCase().includes(search.toLowerCase()),
                )
                .map((i) => {
                  const guidance = serviceGuidance(
                    { ...site, serviceProfile: draft },
                    g.id,
                    i.id,
                    i.elements[0].id,
                  );
                  return (
                    <details className="cqc-card" key={i.id}>
                      <summary>
                        <strong>{i.label}</strong> ·{" "}
                        {
                          i.elements.filter(
                            (e) =>
                              draft.decisions[scopeKey(g.id, i.id, e.id)]
                                ?.mode === "required",
                          ).length
                        }{" "}
                        required
                      </summary>
                      <p>{guidance.focus}</p>
                      {guidance.context.map((c) => (
                        <p key={c} className="cqc-muted">
                          {c}
                        </p>
                      ))}
                      {guidance.functionNotes.map((c) => (
                        <p key={c}>{c}</p>
                      ))}
                      {i.elements.map((e) => {
                        const k = scopeKey(g.id, i.id, e.id),
                          d = draft.decisions[k] || {
                            mode: "required",
                            reason: "Included after review",
                          };
                        return (
                          <div className="cqc-scope-row" key={k}>
                            <label htmlFor={`scope-${k}`}>{e.label}</label>
                            <select
                              id={`scope-${k}`}
                              value={d.mode}
                              onChange={(ev) =>
                                decision(k, {
                                  mode: ev.target.value,
                                  reason:
                                    ev.target.value === "not-applicable"
                                      ? ""
                                      : d.reason,
                                })
                              }
                            >
                              <option value="required">Required</option>
                              <option value="supporting">Supporting</option>
                              <option value="not-applicable">
                                Not applicable
                              </option>
                            </select>
                            <input
                              aria-label={`Reason for ${i.label} — ${e.label}`}
                              maxLength={1000}
                              placeholder="Reason / responsibilities / alternative evidence"
                              value={d.reason}
                              onChange={(ev) =>
                                decision(k, { reason: ev.target.value })
                              }
                            />
                          </div>
                        );
                      })}
                    </details>
                  );
                })}
            </section>
          ))}
          <section className="cqc-card">
            <label>
              Profile review date{" "}
              <input
                type="date"
                value={draft.reviewDate}
                onChange={(e) => patch({ reviewDate: e.target.value })}
              />
            </label>
            <p>
              <label>
                Profile notes{" "}
                <textarea
                  style={{ width: "100%" }}
                  maxLength={2000}
                  value={draft.notes}
                  onChange={(e) => patch({ notes: e.target.value })}
                />
              </label>
            </p>
            <label>
              <input
                type="checkbox"
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
              />{" "}
              I have reviewed the services, selected national framework,
              responsibilities and evidence selections, including the reasons
              for supporting or excluded items.
            </label>
            <p className="cqc-muted">
              Existing evidence and completion records are retained when the
              plan changes. Changes are recorded with your account and a
              revision number.
            </p>
          </section>
        </section>
      )}
      <div className="cqc-toolbar">
        <button className="cqc-button" disabled={busy} onClick={onCancel}>
          Cancel
        </button>
        {step > 0 && (
          <button
            className="cqc-button"
            disabled={busy}
            onClick={() => setStep((n) => n - 1)}
          >
            Previous
          </button>
        )}
        {step < 2 ? (
          <button
            className="cqc-button active"
            disabled={!draft.serviceTypes.length || !!selected.issues.length}
            onClick={() => goStep(step + 1)}
          >
            Next
          </button>
        ) : (
          <button
            className="cqc-button active"
            disabled={
              busy ||
              !confirmed ||
              !draft.serviceTypes.length ||
              !!selected.issues.length
            }
            onClick={save}
          >
            {busy ? "Saving…" : "Save profile and evidence plan"}
          </button>
        )}
      </div>
    </div>
  );
}
